import React, { useEffect, useState } from 'react';
import { Loader2, ParkingSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { transportApi } from '@/services/transportApi';

const HOME = '__home__';

/** Driver creates his own empty run: Parking → first pickup, or last drop → Parking. */
export const EmptyRunDialog = ({ target, onClose, onDone }) => {
  const [opts, setOpts] = useState(null);
  const [parking, setParking] = useState(HOME);
  const [busy, setBusy] = useState(false);
  const toPickup = target?.direction === 'TO_PICKUP';

  useEffect(() => {
    if (!target) return;
    setOpts(null);
    transportApi.emptyRunOptions(target.trip.id, target.direction)
      .then((o) => { setOpts(o); setParking(o.home_base ? HOME : (o.locations[0]?.id || '')); })
      .catch((e) => { toast.error(e.response?.data?.detail || 'Cannot create an empty run'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const parkingName = parking === HOME ? (opts?.home_base?.label || 'Home base') : (opts?.locations.find((l) => l.id === parking)?.name || '');
  const submit = async () => {
    setBusy(true);
    try {
      await transportApi.createEmptyRun({ source_trip_id: target.trip.id, direction: target.direction, parking_location_id: parking === HOME ? null : parking });
      toast.success('Empty run created — tap Start Empty Run and photograph the odometer');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="empty-run-dialog">
        <DialogHeader>
          <DialogTitle>{toPickup ? 'Empty run: Parking → Pickup' : 'Empty run: Drop → Parking'}</DialogTitle>
          <DialogDescription>Drive the empty vehicle {toPickup ? 'from parking to the first pickup' : 'from the last drop back to parking'} — km is recorded from odometer photos as an Empty run.</DialogDescription>
        </DialogHeader>
        {!opts ? <p className="text-sm text-gray-500 flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</p> : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Parking</Label>
              <Select value={parking} onValueChange={setParking}>
                <SelectTrigger data-testid="empty-run-parking-select"><SelectValue placeholder="Choose parking" /></SelectTrigger>
                <SelectContent>
                  {opts.home_base && <SelectItem value={HOME}>{opts.home_base.label} (home base)</SelectItem>}
                  {opts.locations.filter((l) => l.id !== opts.home_base?.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm bg-gray-50 rounded px-3 py-2" data-testid="empty-run-route">
              <ParkingSquare className="inline w-4 h-4 mr-1 text-indigo-600" />
              {toPickup ? <>{parkingName} → <b>{opts.anchor.label}</b></> : <><b>{opts.anchor.label}</b> → {parkingName}</>}
              <span className="text-gray-400"> · {opts.vehicle_label}</span>
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="empty-run-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !opts || !parking} className="bg-indigo-600 hover:bg-indigo-700" data-testid="empty-run-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Create empty run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmptyRunDialog;
